import React from 'react';
import styled from 'styled-components';
import { Building2, Clock, Globe, GraduationCap, MapPin, Sparkles, Users } from 'lucide-react';
import { colors, spacing, typography, borderRadius, breakpoints } from '../../styles/theme';
import type { Event } from '../../types/api';
import { useTranslations } from '../../locales/useI18n';
import {
  EVENT_FORMAT_LABELS,
  getEventFormat,
  getRelationshipBadges,
  getTimeOfDay,
  getUniversityHosts,
  getVibes,
  type EventEntityRef,
} from '../../utils/eventPresentation';

/**
 * Shared badge vocabulary used on entity pages, search results, home cards,
 * grid cards, map popups, and event details. Every badge carries visible text
 * (never colour alone) and a stable accessible name.
 */

type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'info';

const TONES: Record<BadgeTone, { bg: string; fg: string; border: string }> = {
  neutral: { bg: colors.gray[100], fg: colors.gray[800], border: colors.gray[300] },
  primary: { bg: colors.primary[50], fg: colors.primary[800], border: colors.primary[200] },
  success: { bg: colors.success[50], fg: colors.success[800], border: colors.success[200] },
  warning: { bg: colors.warning[50], fg: colors.warning[800], border: colors.warning[300] },
  info: { bg: colors.secondary[50], fg: colors.secondary[800], border: colors.secondary[200] },
};

const BadgeRoot = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: BadgeTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.full};
  border: 1px solid ${(props) => TONES[props.tone].border};
  background: ${(props) => TONES[props.tone].bg};
  color: ${(props) => TONES[props.tone].fg};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  line-height: 1.5;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;

  svg {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
  }
`;

const BadgeLink = styled(BadgeRoot).attrs({ as: 'a' })`
  text-decoration: none;

  &:hover,
  &:focus-visible {
    filter: brightness(0.96);
    text-decoration: underline;
  }
`;

export const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
  min-width: 0;

  @media (max-width: ${breakpoints.sm}) {
    gap: ${spacing[1]};
  }
`;

interface BadgeProps {
  tone?: BadgeTone;
  icon?: React.ReactNode;
  children: React.ReactNode;
  title?: string;
  href?: string;
  onClick?: (event: React.MouseEvent) => void;
}

export const Badge: React.FC<BadgeProps> = ({ tone = 'neutral', icon, children, title, href, onClick }) => {
  if (href) {
    return (
      <BadgeLink tone={tone} href={href} title={title} onClick={onClick}>
        {icon}
        {children}
      </BadgeLink>
    );
  }
  return (
    <BadgeRoot tone={tone} title={title}>
      {icon}
      {children}
    </BadgeRoot>
  );
};

const FORMAT_TONE: Record<string, BadgeTone> = {
  'in-person': 'success',
  virtual: 'primary',
  hybrid: 'info',
};

const FORMAT_ICON: Record<string, React.ReactNode> = {
  'in-person': <MapPin aria-hidden="true" />,
  virtual: <Globe aria-hidden="true" />,
  hybrid: <Globe aria-hidden="true" />,
};

export const FormatBadge: React.FC<{ event: Partial<Event> }> = ({ event }) => {
  const format = getEventFormat(event);
  return (
    <Badge tone={FORMAT_TONE[format]} icon={FORMAT_ICON[format]} title={`Format: ${EVENT_FORMAT_LABELS[format]}`}>
      {EVENT_FORMAT_LABELS[format]}
    </Badge>
  );
};

export const TimeOfDayBadge: React.FC<{ event: Partial<Event> }> = ({ event }) => {
  const label = getTimeOfDay(event);
  if (!label) return null;
  return (
    <Badge tone="warning" icon={<Clock aria-hidden="true" />} title={`Time of day: ${label}`}>
      {label}
    </Badge>
  );
};

export const VibeBadges: React.FC<{ event: Partial<Event> }> = ({ event }) => {
  const vibes = getVibes(event);
  if (vibes.length === 0) return null;
  return (
    <>
      {vibes.map((vibe) => (
        <Badge key={vibe} tone="info" icon={<Sparkles aria-hidden="true" />} title={`Vibe: ${vibe}`}>
          {vibe}
        </Badge>
      ))}
    </>
  );
};

export const UniversityHostedBadge: React.FC<{
  event: Partial<Event>;
  hosts?: EventEntityRef[];
}> = ({ event, hosts }) => {
  const { translations } = useTranslations();
  const resolvedHosts = hosts ?? getUniversityHosts(event);
  if (resolvedHosts.length === 0) return null;
  const names = resolvedHosts
    .map(host => host.displayName || host.name || host.entityName)
    .filter(Boolean)
    .join(', ');
  return (
    <Badge
      tone="warning"
      icon={<GraduationCap aria-hidden="true" />}
      title={names ? `Hosted by ${names}` : 'Hosted by a university'}
    >
      {translations.events.universityHosted}
    </Badge>
  );
};

interface RelationshipBadgesProps {
  event: Partial<Event>;
  /** When set, that entity's badge shows the bare relationship (entity pages). */
  contextEntityId?: number | null;
  /** How many badges to show before collapsing into "+N more". */
  maxVisible?: number;
}

export const RelationshipBadges: React.FC<RelationshipBadgesProps> = ({
  event,
  contextEntityId,
  maxVisible = 2,
}) => {
  const badges = getRelationshipBadges(event, contextEntityId);
  if (badges.length === 0) return null;

  const visible = badges.slice(0, maxVisible);
  const hidden = badges.slice(maxVisible);

  return (
    <>
      {visible.map((badge) => (
        <Badge
          key={badge.key}
          tone="primary"
          icon={<Building2 aria-hidden="true" />}
          title={`Relationship: ${badge.label}`}
        >
          {badge.label}
        </Badge>
      ))}
      {hidden.length > 0 && (
        <Badge tone="neutral" icon={<Users aria-hidden="true" />} title={hidden.map((b) => b.label).join(', ')}>
          {`+${hidden.length} more`}
        </Badge>
      )}
    </>
  );
};

interface EventBadgesProps {
  event: Partial<Event>;
  contextEntityId?: number | null;
  /** Detail pages show every relationship; listings collapse after two. */
  maxRelationships?: number;
}

/** The complete, consistently ordered badge set for an event. */
export const EventBadges: React.FC<EventBadgesProps> = ({ event, contextEntityId, maxRelationships }) => (
  <BadgeRow>
    <UniversityHostedBadge event={event} />
    <RelationshipBadges event={event} contextEntityId={contextEntityId} maxVisible={maxRelationships} />
    <FormatBadge event={event} />
    <TimeOfDayBadge event={event} />
    <VibeBadges event={event} />
  </BadgeRow>
);

export default EventBadges;
